import { useCallback, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import AsyncSelectLib from "react-select/async";
import { Label } from "reactstrap";

export const AsyncSelect = ({
  name,
  control,
  label,
  loadOptions,
  isMulti = false,
  defaultValue = [],
  rules = {},
  placeholder = "",
  styles = {},
  getOptionValue = (option) => option.value,
  getOptionLabel = (option) => option.label,
  cacheOptions = false,
  defaultOptions = false,
  isLocal = false,
  disabledMarginBottom,
  prefetchOnMount = true,
  ...restProps
}) => {
  const [focusLoaded, setFocusLoaded] = useState(false);
  const [dynamicOptions, setDynamicOptions] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [themeVars, setThemeVars] = useState({});

  // Use ref to track loading state without triggering re-renders in callbacks
  const loadingRef = useRef(false);
  const isRequired = !!rules?.required;

  // ─── Theme observer ───────────────────────────────────────────────────────
  useEffect(() => {
    const updateThemeVars = () => {
      const root = document.documentElement;
      setThemeVars({
        inputBg:      getComputedStyle(root).getPropertyValue("--vz-input-bg-custom").trim(),
        bodyColor:    getComputedStyle(root).getPropertyValue("--vz-body-color").trim(),
        borderColor:  getComputedStyle(root).getPropertyValue("--vz-input-border-custom").trim(),
        tertiaryBg:   getComputedStyle(root).getPropertyValue("--vz-tertiary-bg").trim(),
        invalidColor: getComputedStyle(root).getPropertyValue("--vz-form-invalid-color").trim(),
        primaryColor: getComputedStyle(root).getPropertyValue("--vz-primary").trim(),
        whiteColor:   getComputedStyle(root).getPropertyValue("--vz-white").trim(),
        dangerColor:  getComputedStyle(root).getPropertyValue("--vz-danger").trim(),
      });
    };

    updateThemeVars();
    const observer = new MutationObserver(updateThemeVars);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-bs-theme", "class"],
    });
    return () => observer.disconnect();
  }, []);

  // ─── Prefetch on mount ────────────────────────────────────────────────────
  // Only fires if prefetchOnMount=true. Sets focusLoaded=true so handleFocus
  // won't duplicate the call.
  useEffect(() => {
    if (!prefetchOnMount || typeof loadOptions !== "function") return;
    if (loadingRef.current || focusLoaded) return;

    loadingRef.current = true;
    setLocalLoading(true);

    loadOptions("")
      .then((options) => {
        setDynamicOptions(options ?? []);
        setFocusLoaded(true);
      })
      .catch(console.warn)
      .finally(() => {
        setLocalLoading(false);
        loadingRef.current = false;
      });
  }, [prefetchOnMount]); // loadOptions intentionally omitted — treat as stable ref

  // ─── Lazy fetch on focus (only if not pre-fetched) ────────────────────────
  const handleFocus = useCallback(async () => {
    if (focusLoaded || loadingRef.current || typeof loadOptions !== "function") return;

    loadingRef.current = true;
    setLocalLoading(true);
    try {
      const options = await loadOptions("");
      setDynamicOptions(options ?? []);
      setFocusLoaded(true);
    } catch (err) {
      console.warn("AsyncSelect focus load error:", err);
    } finally {
      setLocalLoading(false);
      loadingRef.current = false;
    }
  }, [focusLoaded, loadOptions]);

  // ─── Wrapped load for typing/search ──────────────────────────────────────
  // isLocal=true  → filter already-fetched options client-side (no API call)
  // isLocal=false → call loadOptions(inputValue) on each keystroke
  const wrappedLoadOptions = useCallback(
    async (inputValue) => {
      if (isLocal && dynamicOptions.length) {
        const lower = inputValue.toLowerCase();
        return dynamicOptions.filter((option) =>
          getOptionLabel(option).toLowerCase().includes(lower)
        );
      }

      try {
        const options = await loadOptions(inputValue);
        // Cache first result set if not yet stored
        if (!focusLoaded) {
          setDynamicOptions(options ?? []);
          setFocusLoaded(true);
        }
        return options ?? [];
      } catch (err) {
        console.warn("AsyncSelect loadOptions error:", err);
        return [];
      }
    },
    [isLocal, dynamicOptions, getOptionLabel, loadOptions, focusLoaded]
  );

  // ─── Custom styles (theme-aware) ──────────────────────────────────────────
  const buildStyles = useCallback(
    (hasError, isDisabled) => ({
      control: (provided, state) => ({
        ...provided,
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: hasError ? themeVars.invalidColor : themeVars.borderColor,
        boxShadow: state.isFocused
          ? hasError
            ? `0 0 0 1px ${themeVars.invalidColor}`
            : "0 0 0 1px #2684FF"
          : "none",
        "&:hover": {
          borderColor: hasError ? themeVars.invalidColor : "#2684FF",
          cursor: "pointer",
        },
        minHeight: "38px",
        backgroundColor: isDisabled ? themeVars.tertiaryBg : themeVars.inputBg,
        color: themeVars.bodyColor,
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 1 : undefined,
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: themeVars.inputBg,
        color: themeVars.bodyColor,
        zIndex: 9999,
      }),
      menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999,
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
          ? themeVars.primaryColor
          : state.isFocused
          ? `${themeVars.primaryColor}22`
          : themeVars.inputBg,
        color: state.isSelected ? themeVars.whiteColor : themeVars.bodyColor,
        cursor: "pointer",
      }),
      input: (provided) => ({
        ...provided,
        color: themeVars.bodyColor,
      }),
      singleValue: (provided) => ({
        ...provided,
        color: themeVars.bodyColor,
      }),
      multiValue: (provided) => ({
        ...provided,
        backgroundColor: themeVars.primaryColor,
        color: themeVars.whiteColor,
      }),
      multiValueLabel: (provided) => ({
        ...provided,
        color: themeVars.whiteColor,
      }),
      multiValueRemove: (provided) => ({
        ...provided,
        color: themeVars.whiteColor,
        ":hover": {
          backgroundColor: themeVars.dangerColor,
          color: themeVars.whiteColor,
          cursor: "pointer",
        },
      }),
      loadingIndicator: (provided) => ({
        ...provided,
        color: themeVars.primaryColor,
      }),
      placeholder: (provided) => ({
        ...provided,
        color: `${themeVars.bodyColor}88`,
      }),
      // Allow caller to override any style
      ...styles,
    }),
    [themeVars, styles]
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className={disabledMarginBottom ? "mb-0" : "mb-3"}>
      <style>{`.required-asterisk { color: red; margin-left: 4px; }`}</style>

      {label && (
        <Label htmlFor={name} className="form-label">
          {label}
          {isRequired && <span className="required-asterisk">*</span>}
        </Label>
      )}

      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={rules}
        render={({ field, fieldState: { error } }) => {
          const hasError = !!error;
          const isDisabled = restProps.isDisabled || restProps.disabled;

          return (
            <>
              <AsyncSelectLib
                inputId={name}          // use inputId (not id) for react-select a11y
                isMulti={isMulti}
                loadOptions={wrappedLoadOptions}
                placeholder={placeholder}
                getOptionValue={getOptionValue}
                getOptionLabel={getOptionLabel}
                styles={buildStyles(hasError, isDisabled)}
                cacheOptions={cacheOptions}
                defaultOptions={dynamicOptions.length ? dynamicOptions : defaultOptions}
                onFocus={handleFocus}
                onChange={(selected) => field.onChange(selected)}
                onBlur={field.onBlur}
                value={field.value}
                name={field.name}
                ref={field.ref}
                isLoading={localLoading}
                isDisabled={isDisabled}
                menuPortalTarget={document.body}  // prevent modal clipping
                menuPosition="fixed"
                loadingMessage={() => (
                  <div style={{ color: themeVars.bodyColor, padding: "5px 10px" }}>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      style={{ color: themeVars.primaryColor, verticalAlign: "middle" }}
                    />
                    Loading...
                  </div>
                )}
                noOptionsMessage={() => (
                  <div style={{ color: themeVars.bodyColor, padding: "5px 10px" }}>
                    No options found
                  </div>
                )}
                {...restProps}
              />
              {hasError && (
                <div className="invalid-feedback d-block">{error.message}</div>
              )}
            </>
          );
        }}
      />
    </div>
  );
};

export default AsyncSelect;